'use client';

import { useState, useCallback, useEffect, useMemo } from 'react';
import { tmaActions } from '../bootstrap/tmaActions';
import { rawField, stringField } from '../lib/jsonBody';
import { useApiClient } from '../contexts/ApiContext';
import { useProfileData } from '../queries/useProfileQueries';
import type { Runtime } from '../runtime/host';
import { useRuntime } from '../runtime/RuntimeContext';
import { getBotUsername, getMiniAppName } from '../utils/config';
import { shareOrCopyLink } from '../lib/browserSurface';

const GIF_PATH = '/share-referral.mp4';

interface UseShareReferralOptions {
  messageText: string;
  buttonText: string;
  source?: string;
}

interface BuildReferralLinkArgs {
  runtime: Runtime;
  userId: string;
  origin: string;
  source?: string;
}

function buildReferralLink({ runtime, userId, origin, source }: BuildReferralLinkArgs): string {
  if (runtime === 'web') {
    return `${origin}/?ref=${userId}${source ? `&utm_source=${source}` : ''}`;
  }
  return `https://t.me/${getBotUsername()}/${getMiniAppName()}?startapp=ref_${userId}${source ? `--${source}` : ''}`;
}

export function useShareReferral({ messageText, buttonText, source }: UseShareReferralOptions) {
  const [isSharing, setIsSharing] = useState(false);
  const apiClient = useApiClient();
  const profile = useProfileData();
  const runtime = useRuntime();

  const [origin, setOrigin] = useState('');
  useEffect(() => setOrigin(window.location.origin), []);

  const referralLink = useMemo(
    () =>
      profile?.id && (runtime !== 'web' || origin)
        ? buildReferralLink({ runtime, userId: profile.id, origin, source })
        : '',
    [profile?.id, runtime, origin, source],
  );

  const share = useCallback(async () => {
    if (!profile?.id || isSharing) return;

    const link = buildReferralLink({
      runtime,
      userId: profile.id,
      origin: window.location.origin,
      source,
    });

    if (await tmaActions.isShareMessageAvailable()) {
      setIsSharing(true);
      try {
        const [firstLine, ...rest] = messageText.split('\n\n');
        const htmlCaption = `<b>${firstLine}</b>\n\n${rest.join('\n\n')}`;
        const gifUrl = `${window.location.origin}${GIF_PATH}`;

        const { preparedMessageId } = await apiClient.api.userControllerSavePreparedInlineMessage({
          gifUrl,
          caption: htmlCaption,
          title: 'SHOT',
          button: { text: buttonText, url: link },
        });

        await tmaActions.shareMessage(preparedMessageId);
      } catch (error) {
        if (stringField(error, 'message')?.includes('ERR_CANCELLED') || rawField(error, 'type') === 'ERR_CANCELLED') {
          return;
        }
        throw error;
      } finally {
        setIsSharing(false);
      }
    } else if (window.location.hash.includes('tgWebAppData')) {
      const message = encodeURIComponent(messageText);
      await tmaActions.openTelegramLink(`https://t.me/share/url?url=${link}&text=${message}`);
    } else {
      await shareOrCopyLink(messageText, link);
    }
  }, [profile?.id, isSharing, apiClient, messageText, buttonText, source, runtime]);

  return { share, isSharing, referralLink };
}
